import React, { useEffect } from 'react';
import { Select, Input, Tooltip, Form } from 'antd';

import { useWorkflowStore } from '@src/pages/workflow/store';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import CustomFormItem from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/CustomFormItem';
import { renderItemForViewMode } from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/index';
import { isViewMode } from '@src/pages/metric-templates/utils';
import { EFormMode } from '@src/emp-3.0/types/common';
import { EMetricTypes } from '@src/emp-3.0/types';
import { IMetricInformationProps } from './typed';
import { EventLabel } from '@src/emp-3.0/components/Metrics/components';
import { InfoCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const metricTypeOptions = [
  {
    value: EMetricTypes.CONVERSION,
    text: 'Conversion',
    label: (
      <Tooltip title="Calculate the conversion rate and count between events">
        <div>Conversion</div>
      </Tooltip>
    ),
    tooltip: 'Calculate the conversion rate and count between events',
  },
  {
    value: EMetricTypes.DURATION,
    text: 'Duration',
    label: (
      <Tooltip title="Calculate the time duration between events">
        <div>Duration</div>
      </Tooltip>
    ),
    tooltip: 'Calculate the time duration between events',
  },
];

function addUniqueOption(
  options: typeof metricTypeOptions,
  newOption: (typeof metricTypeOptions)[number],
  key: keyof (typeof metricTypeOptions)[number] = 'value',
) {
  const exists = options.some((option) => option[key] === newOption[key]);
  if (!exists) {
    options.push(newOption);
  }
}

// Ensure that `Form.Item` has only one direct form control as its child to properly handle value binding and management.
const MetricTypeItem = ({ metric, form, ...restProps }) => {
  const { formMode } = useWorkflowStore();

  const metricType = Form.useWatch('metrics_type', form);

  const isEverDeployed = formMode === EFormMode.EDIT && metric?.is_ever_deployed;
  const isView = isViewMode(formMode);
  const tooltip = metricTypeOptions.find((i) => i.value === metricType)?.tooltip || '';

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {isView ? (
        renderItemForViewMode(form, 'metrics_type')
      ) : (
        <Select
          style={{ width: 160 }}
          disabled={isEverDeployed}
          options={metricTypeOptions}
          optionLabelProp="text"
          {...restProps}
        />
      )}
      <Tooltip title={tooltip}>
        <InfoCircleOutlined
          style={{ color: 'rgba(0,0,0,0.45)', marginLeft: 8, fontSize: '18px' }}
        />
      </Tooltip>
    </div>
  );
};

const MetricInformation: React.FC<IMetricInformationProps> = ({ form, metric }) => {
  const { formMode } = useWorkflowStore();
  const { userInfo } = useInitialInfoStore();

  const isTlb2 = userInfo.is_tlb2;
  const isView = isViewMode(formMode);
  const description = Form.useWatch('description', form);

  useEffect(() => {
    if (isTlb2) {
      addUniqueOption(metricTypeOptions, {
        value: EMetricTypes.SINGLE_EVENT,
        text: 'Event Aggregation',
        label: (
          <Tooltip title="Calculate the number or values of an event">
            <div>Event Aggregation</div>
          </Tooltip>
        ),
        tooltip: 'Calculate the number or values of an event',
      });
      addUniqueOption(metricTypeOptions, {
        value: EMetricTypes.RATIO_METRIC,
        text: 'Ratio Metric',
        label: (
          <Tooltip title="A metric derived by dividing a numerator (event or predefined metric) by a denominator (event or predefined metric or fixed number)">
            <div>Ratio Metric</div>
          </Tooltip>
        ),
        tooltip:
          'A metric derived by dividing a numerator (event or predefined metric) by a denominator (event or predefined metric or fixed number)',
      });
    }
  }, [isTlb2]);

  return (
    <>
      <CustomFormItem
        name="description"
        label={
          <EventLabel
            prefix={null}
            text={
              <>
                <div>Description</div>
                <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontWeight: 400 }}>(Optional)</div>
              </>
            }
          />
        }
        labelCol={{ span: 6 }}
        labelAlign="left"
      >
        {isView ? (
          renderItemForViewMode(form, 'description', description)
        ) : (
          <TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={500} />
        )}
      </CustomFormItem>
      <CustomFormItem
        name="metrics_type"
        label={<EventLabel prefix={null} text="Metrics Type" />}
        labelCol={{ span: 6 }}
        labelAlign="left"
      >
        <MetricTypeItem metric={metric} form={form} />
      </CustomFormItem>
    </>
  );
};

export default MetricInformation;
